import { getBrandTraits } from '@/lib/brands/traits';
import type { Brand } from '@/lib/cms/config';
import { BRAND } from '@/lib/cms/config';
import { buildCanonicalUrl } from './canonical';
import { getBaseUrl, getSeoConstants } from './constants';

export interface JsonLdWebSite {
  '@context': 'https://schema.org';
  '@type': 'WebSite';
  name: string;
  url: string;
  potentialAction?: {
    '@type': 'SearchAction';
    target: {
      '@type': 'EntryPoint';
      urlTemplate: string;
    };
    'query-input': string;
  };
}

export interface JsonLdOrganization {
  '@context': 'https://schema.org';
  '@type': 'Organization';
  name: string;
  legalName: string;
  foundingDate: string;
  url: string;
  logo: string;
  sameAs: string[];
  contactPoint: Array<{
    '@type': 'ContactPoint';
    telephone: string;
    contactType: string;
    areaServed: string;
    availableLanguage: string[];
  }>;
}

export interface JsonLdBreadcrumbItem {
  name: string;
  url: string;
}

export interface JsonLdBreadcrumbList {
  '@context': 'https://schema.org';
  '@type': 'BreadcrumbList';
  itemListElement: Array<{
    '@type': 'ListItem';
    position: number;
    name: string;
    item: string;
  }>;
}

/**
 * Build WebSite structured data for the homepage, with a SearchAction when the
 * brand has site search.
 * Per spec §7: tells search engines about site search functionality.
 */
export function buildWebSiteSchema(locale: string, brand: Brand = BRAND): JsonLdWebSite {
  const constants = getSeoConstants(brand);
  const baseUrl = getBaseUrl(brand);
  const schema: JsonLdWebSite = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: constants.brandName,
    url: `${baseUrl}/${locale}/`,
  };

  if (!getBrandTraits(brand).hasSiteSearch) return schema;

  return {
    ...schema,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${baseUrl}/${locale}/search/all?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * Build Organization structured data for the homepage.
 * Per spec §7: includes brand name, logo, social profiles, and contact point.
 */
export function buildOrganizationSchema(brand: Brand = BRAND): JsonLdOrganization {
  const constants = getSeoConstants(brand);
  const baseUrl = getBaseUrl(brand);
  const org = constants.organization;

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: org.name,
    legalName: org.legalName,
    foundingDate: org.foundingDate,
    url: baseUrl,
    logo: org.logo,
    sameAs: org.sameAs,
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: org.contactPoint.telephone,
        contactType: org.contactPoint.contactType,
        areaServed: org.contactPoint.areaServed,
        availableLanguage: org.contactPoint.availableLanguage,
      },
    ],
  };
}

/**
 * Build BreadcrumbList structured data.
 * Per spec §7: valid schema.org BreadcrumbList with positional items.
 */
export function buildBreadcrumbSchema(items: JsonLdBreadcrumbItem[]): JsonLdBreadcrumbList {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

const SCHEMA_ORG = 'https://schema.org/';

export type ProductAvailability = 'InStock' | 'PreOrder' | 'OutOfStock';

export function mapCtaToSchemaAvailability(
  ctaType: string | undefined,
  isCatalogMode: boolean
): ProductAvailability {
  if (isCatalogMode) {
    return 'OutOfStock';
  }

  switch (ctaType) {
    case 'ORDER':
      return 'InStock';
    case 'PRE_ORDER':
      return 'PreOrder';
    default:
      return 'OutOfStock';
  }
}

export interface BuildProductSchemaInput {
  name: string;
  description?: string;
  sku: string;
  image?: string;
  url: string;
  price: number;
  currency: string;
  availability: ProductAvailability;
}

function buildOfferSchema(input: BuildProductSchemaInput) {
  return {
    '@type': 'Offer',
    url: input.url,
    price: input.price,
    priceCurrency: input.currency,
    availability: `${SCHEMA_ORG}${input.availability}`,
    itemCondition: `${SCHEMA_ORG}NewCondition`,
  };
}

/**
 * Per spec §7. Brands without online sales omit `offers`, so search engines
 * show neither a price nor a stock state instead of a false "Out of stock".
 */
export function buildProductSchema(input: BuildProductSchemaInput, brand: Brand = BRAND) {
  const includesOffer = getBrandTraits(brand).pdp.structuredDataIncludesOffer;

  return {
    '@context': SCHEMA_ORG,
    '@type': 'Product',
    name: input.name,
    description: input.description,
    sku: input.sku,
    image: input.image ? [input.image] : undefined,
    url: input.url,
    brand: {
      '@type': 'Brand',
      name: getSeoConstants(brand).brandName,
    },
    offers: includesOffer ? buildOfferSchema(input) : undefined,
  };
}

/**
 * Build PDP JSON-LD from shared route fields (watches + straps).
 *
 * Example Input:
 * { name:'Watch', sku:'ABC', pdpPath:'/us-en/watches/a/b/ABC', locale:'us-en', price:100, currency:'USD', ctaType:'PRE_ORDER', isCatalogMode:false }
 *
 * Example Output:
 * Product schema with availability PreOrder
 */
export function buildPdpProductJsonLd(params: {
  name: string;
  description?: string | null;
  sku: string;
  imageUrl?: string;
  pdpPath: string;
  locale: string;
  price: number;
  currency: string;
  ctaType: string | undefined;
  isCatalogMode: boolean;
  brand?: Brand;
}) {
  return buildProductSchema(
    {
      name: params.name,
      description: params.description ?? undefined,
      sku: params.sku,
      image: params.imageUrl,
      url: buildCanonicalUrl({ path: params.pdpPath, locale: params.locale }),
      price: params.price,
      currency: params.currency,
      availability: mapCtaToSchemaAvailability(params.ctaType, params.isCatalogMode),
    },
    params.brand
  );
}
